import React, { useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  FaMapMarkerAlt,
  FaStar,
  FaArrowLeft,
  FaImages,
  FaChevronUp,
  FaWhatsapp,
  FaEnvelope,
} from "react-icons/fa";
import { tourPackages, speedBoatData, luxuryYacht } from "../data/dummyData";
import SEO from "../components/common/SEO";
import {
  getSpeedBoatTourSchema,
  getBreadcrumbSchema,
} from "../utils/structuredData";

const NewBooking = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [showAllImages, setShowAllImages] = useState(false);
  const mainImageRef = useRef(null);
  const bookingCardRef = useRef(null);

  const slowScroll = (targetY, duration = 900) => {
    const startY = window.scrollY;
    const diff = targetY - startY;
    if (Math.abs(diff) < 5) return;
    let start = null;
    const ease = (t) =>
      t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
    const step = (ts) => {
      if (!start) start = ts;
      const elapsed = ts - start;
      const p = Math.min(elapsed / duration, 1);
      window.scrollTo(0, startY + diff * ease(p));
      if (elapsed < duration) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  };

  const slowScrollToElement = (el, duration = 900, offset = 90) => {
    if (!el) return;
    const targetY = window.scrollY + el.getBoundingClientRect().top - offset;
    slowScroll(Math.max(0, targetY), duration);
  };

  // Combine all services
  const allServices = [
    ...(tourPackages || []),
    ...(speedBoatData || []),
    ...(luxuryYacht || []),
  ];

  const service = allServices.find((item) => item.slug === slug);

  // Yacht and Jet Ski are request-only — no calendar booking
  const isYachtRequest = slug === "luxury-yacht-durres";
  const isJetSkiRequest = slug === "jet-ski-adventure";

  // Initialize selected image when service loads
  React.useEffect(() => {
    if (service) {
      setSelectedImage(service.image || service.gallery?.[0]);
    }
  }, [service]);

  // Handle package selection and gently hint-scroll toward the booking card
  const handlePackageSelection = (packageNumber) => {
    setSelectedPackage(packageNumber);
    // Smooth ease-out sine nudge — glides to a stop with no abrupt snap
    const el = bookingCardRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    if (rect.top > 100) {
      const nudge = Math.min(180, rect.top - 100);
      // const targetY = window.scrollY + nudge;
      const startY = window.scrollY;
      const duration = 1800;
      let start = null;
      const easeOutSine = (t) => Math.sin((t * Math.PI) / 2);
      const step = (ts) => {
        if (!start) start = ts;
        const p = Math.min((ts - start) / duration, 1);
        window.scrollTo(0, startY + nudge * easeOutSine(p));
        if (p < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    }
  };

  if (!service) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="bg-white rounded-2xl shadow-xl px-8 py-12 text-center max-w-md">
          <span className="text-5xl mb-4 block">😕</span>
          <h2 className="text-2xl font-bold text-red-500 mb-2">
            Service not found
          </h2>
          <p className="text-gray-600 mb-6">
            The service you're looking for doesn't exist or has been removed.
          </p>
          <button
            onClick={() => navigate("/")}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all"
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  const seoBySlug = {
    "quicksilver-tour": {
      title: "Boat Cruise Durrës Albania | Speedboat Tour – SeaPulse",
      description:
        "Book a 90-min Boat Cruise in Durrës, Albania from €50/person. 5 daily departures: Morning, Midday, Afternoon, Evening & Night. Up to 11 guests. Instant online booking.",
    },
    "bayliner-water-sports": {
      title: "Bayliner Water Sports Durrës Albania | SeaPulse",
      description:
        "Book Bayliner water sports sessions in Durrës, Albania. 30 or 60-min group sessions from €100. Up to 4 people. Instant online booking with SeaPulse Albania.",
    },
    "luxury-yacht-durres": {
      title: "Luxury Yacht Charter Durrës Albania | SeaPulse",
      description:
        "Private luxury yacht charter in Durrës, Albania. Perfect for weddings, corporate events & special occasions. Request a quote with SeaPulse Albania.",
    },
    "jet-ski-adventure": {
      title: "Jet Ski Rental Durrës Albania | SeaPulse",
      description:
        "Rent a jet ski in Durrës, Albania from €60. 15 or 30-minute thrilling sessions on the Adriatic Sea. Book online with SeaPulse Albania.",
    },
  };
  const pageSeo = seoBySlug[slug] || {
    title: `${(service.name || "").trim()} | Book Online – Durrës Albania`,
    description:
      (service.description || "").slice(0, 155) ||
      "Book sea tours in Durrës, Albania with SeaPulse. Professional crew, instant online booking.",
  };

  return (
    <div className="min-h-screen pt-16 sm:pt-20 md:pt-24 px-3 sm:px-4 md:px-6 lg:px-10 pb-6 sm:pb-8 md:pb-16 bg-gray-50">
      <SEO
        title={pageSeo.title}
        description={pageSeo.description}
        canonical={`/${slug}/tour-details`}
        structuredData={[
          getSpeedBoatTourSchema(),
          getBreadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Boat Tours", path: "/boat-tours" },
            {
              name: (service.name || "").trim(),
              path: `/${slug}/tour-details`,
            },
          ]),
        ]}
      />
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-3 sm:mb-4 md:mb-6 transition-colors text-xs sm:text-sm md:text-base"
        >
          <FaArrowLeft className="text-xs sm:text-sm" />
          <span>Back</span>
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-6 lg:gap-8">
          {/* Main Content - 2/3 width */}
          <div className="lg:col-span-2 space-y-3 sm:space-y-4 md:space-y-6">
            {/* Title and Location - Above Image */}
            <div className="bg-white rounded-lg sm:rounded-xl md:rounded-2xl shadow-lg p-3 sm:p-4 md:p-6">
              <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-2 sm:mb-3">
                {service.name}
              </h1>

              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4 text-xs sm:text-sm md:text-base">
                {service.location && (
                  <div className="flex items-center gap-1.5 sm:gap-2 text-gray-600">
                    <FaMapMarkerAlt className="text-blue-600 text-xs sm:text-sm" />
                    <span>{service.location}</span>
                  </div>
                )}
                {service.rating && (
                  <div className="flex items-center gap-1.5 sm:gap-2">
                    <FaStar className="text-yellow-500 text-xs sm:text-sm" />
                    <span className="font-semibold">{service.rating}</span>
                    {service.reviews && (
                      <span className="text-gray-600">
                        ({service.reviews} reviews)
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Image Gallery */}
            <div className="bg-white rounded-lg sm:rounded-xl md:rounded-2xl shadow-lg overflow-hidden">
              <img
                ref={mainImageRef}
                src={selectedImage || service.image || service.gallery?.[0]}
                alt={service.name}
                className="w-full h-72 sm:h-[350px] md:h-[450px] lg:h-[550px] object-cover rounded-xl shadow-xl"
              />
              {service.gallery && service.gallery.length > 1 && (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 md:gap-4 p-2 sm:p-3 md:p-5">
                    {(showAllImages
                      ? service.gallery
                      : service.gallery.slice(0, 4)
                    ).map((img, index) => (
                      <img
                        key={index}
                        src={img}
                        alt={`Gallery ${index + 1}`}
                        onClick={() => {
                          setSelectedImage(img);
                          slowScrollToElement(mainImageRef.current, 700);
                        }}
                        className={`w-full h-24 sm:h-28 md:h-32 lg:h-36 object-cover rounded-xl cursor-pointer hover:opacity-80 transition-all ${
                          selectedImage === img
                            ? "ring-2 ring-blue-500 opacity-100"
                            : ""
                        }`}
                      />
                    ))}
                  </div>
                  {service.gallery.length > 4 && (
                    <div className="px-2 sm:px-3 md:px-5 pb-2 sm:pb-3 md:pb-5">
                      <button
                        onClick={() => setShowAllImages(!showAllImages)}
                        className="w-full flex items-center justify-center gap-2 py-3 sm:py-3.5 text-sm sm:text-base font-semibold text-gray-700 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-700 hover:from-gray-100 hover:to-gray-200 dark:hover:from-gray-700 dark:hover:to-gray-600 border border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 rounded-xl transition-all duration-300 shadow-sm hover:shadow-md transform hover:-translate-y-0.5"
                      >
                        {showAllImages ? (
                          <>
                            <FaChevronUp className="text-base" />
                            <span>Show Less</span>
                          </>
                        ) : (
                          <>
                            <FaImages className="text-base" />
                            <span>
                              View All {service.gallery.length} Photos
                            </span>
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Service Info */}
            <div className="bg-white rounded-lg sm:rounded-xl md:rounded-2xl shadow-lg p-3 sm:p-4 md:p-6 lg:p-8">
              {/* Package Cards - Dynamic rendering */}
              {service.packages && service.packages.length > 0 ? (
                <div className="w-full">
                  <h2 className="flex items-center gap-2 text-base sm:text-lg md:text-xl lg:text-2xl font-bold text-gray-900 mb-4 sm:mb-6 md:mb-8">
                    <img
                      src="/logo-light-bg.png"
                      alt="SeaPulse Logo"
                      className="w-8 h-8 sm:w-10 sm:h-10"
                    />
                    Choose Your Package
                  </h2>
                  <div
                    className={`grid grid-cols-1 gap-5 sm:gap-7 ${
                      service.packages.length === 1
                        ? "md:grid-cols-1 max-w-md"
                        : "md:grid-cols-2"
                    }`}
                  >
                    {service.packages.map((pkg, pkgIndex) => {
                      const gradients = [
                        "from-cyan-500 via-blue-600 to-blue-700",
                        "from-teal-500 via-emerald-500 to-cyan-600",
                      ];
                      const icons = ["⚓", "🌊"];
                      const accentColors = [
                        "border-blue-500 shadow-blue-100",
                        "border-emerald-500 shadow-emerald-100",
                      ];
                      const badgeColors = [
                        "bg-amber-400 text-amber-900",
                        "bg-cyan-400 text-cyan-900",
                      ];
                      const btnColors = [
                        "bg-blue-600 hover:bg-blue-700 border-blue-600 hover:border-blue-700",
                        "bg-emerald-600 hover:bg-emerald-700 border-emerald-600 hover:border-emerald-700",
                      ];
                      const isSelected = selectedPackage === pkg.id;
                      const idx = pkgIndex % 2;
                      return (
                        <div
                          key={pkg.id}
                          className={`relative bg-white rounded-2xl overflow-hidden shadow-md border-2 transition-all duration-300 hover:shadow-xl hover:-translate-y-1 ${
                            isSelected
                              ? `${accentColors[idx]} shadow-lg`
                              : "border-gray-100 hover:border-gray-300"
                          }`}
                        >
                          {/* Hero image with gradient overlay */}
                          <div className="relative h-52 sm:h-60 overflow-hidden">
                            <img
                              src={pkg.image}
                              alt={pkg.name}
                              className="w-full h-full object-cover"
                            />
                            <div
                              className={`absolute inset-0 bg-gradient-to-t ${gradients[idx]} opacity-70`}
                            />
                            {/* Badge top-left */}
                            {pkg.badge && (
                              <span
                                className={`absolute top-3 left-3 ${badgeColors[idx]} text-[11px] font-bold px-2.5 py-1 rounded-full shadow`}
                              >
                                {pkg.badge}
                              </span>
                            )}
                            {/* Selected checkmark */}
                            {isSelected && (
                              <div className="absolute top-3 right-3 w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-md">
                                <svg
                                  className="w-5 h-5 text-blue-600"
                                  fill="currentColor"
                                  viewBox="0 0 20 20"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                              </div>
                            )}
                            {/* Title overlay on image */}
                            <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-5">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-2xl">{icons[idx]}</span>
                                <h3 className="text-white text-xl sm:text-2xl font-extrabold drop-shadow">
                                  {pkg.name}
                                </h3>
                              </div>
                              <div className="flex items-center gap-3">
                                <span className="bg-white/20 backdrop-blur-sm text-white text-xs font-semibold px-2.5 py-1 rounded-full border border-white/30">
                                  ⏱ {pkg.duration}
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Card body */}
                          <div className="p-5 sm:p-6">
                            <p className="text-sm text-gray-600 mb-5 leading-relaxed">
                              {pkg.description}
                            </p>

                            {/* Price highlight */}
                            <div className="flex items-end justify-between mb-5 pb-5 border-b border-gray-100">
                              {pkg.price_per_person && (
                                <div>
                                  <p className="text-xs text-gray-400 font-medium uppercase tracking-wider mb-0.5">
                                    Price per person
                                  </p>
                                  <p className="text-4xl font-extrabold text-gray-900">
                                    €{pkg.price_per_person}
                                  </p>
                                </div>
                              )}
                              {pkg.price_per_group && (
                                <div className="text-right">
                                  <p className="text-xs text-gray-400 font-medium uppercase tracking-wider mb-0.5">
                                    Group price
                                  </p>
                                  <p className="text-xl font-bold text-gray-700">
                                    €{pkg.price_per_group}
                                  </p>
                                </div>
                              )}
                            </div>

                            {/* Included list */}
                            <ul className="space-y-2.5 mb-6">
                              {pkg.included &&
                                pkg.included.map((item, index) => (
                                  <li
                                    key={index}
                                    className="flex items-center gap-2.5"
                                  >
                                    <span className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                                      <svg
                                        className="w-3 h-3 text-green-600"
                                        fill="currentColor"
                                        viewBox="0 0 20 20"
                                      >
                                        <path
                                          fillRule="evenodd"
                                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                          clipRule="evenodd"
                                        />
                                      </svg>
                                    </span>
                                    <span className="text-sm text-gray-700">
                                      {item}
                                    </span>
                                  </li>
                                ))}
                            </ul>

                            <button
                              onClick={() => handlePackageSelection(pkg.id)}
                              className={`w-full py-3 rounded-xl font-semibold text-sm sm:text-base transition-all duration-200 border-2 ${
                                isSelected
                                  ? `${btnColors[idx]} text-white shadow-md`
                                  : "bg-white text-gray-800 border-gray-200 hover:border-gray-400"
                              }`}
                            >
                              {isSelected
                                ? "✓ Package Selected"
                                : "Select This Package"}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="prose prose-sm sm:prose-base lg:prose-lg max-w-none">
                  <h2 className="text-base sm:text-lg md:text-xl lg:text-2xl font-semibold mb-2 sm:mb-3 md:mb-4">
                    About this experience
                  </h2>
                  <p className="text-gray-700 leading-relaxed text-xs sm:text-sm md:text-base">
                    {service.description ||
                      service.additionalInfo ||
                      "Experience the best of what we have to offer with this amazing service."}
                  </p>
                </div>
              )}

              {/* Features/Highlights */}
              {service.features && service.features.length > 0 && (
                <div className="mt-4 sm:mt-6 md:mt-8">
                  <h3 className="text-base sm:text-lg md:text-xl lg:text-2xl font-semibold mb-2 sm:mb-3 md:mb-4">
                    What's included
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 sm:gap-3">
                    {service.features.map((feature, index) => (
                      <div
                        key={index}
                        className="flex items-start gap-2 sm:gap-3"
                      >
                        <div className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <svg
                            className="w-2.5 h-2.5 sm:w-3 sm:h-3 md:w-4 md:h-4 text-green-600"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </div>
                        <span className="text-gray-700 text-xs sm:text-sm md:text-base">
                          {feature}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Booking Card - 1/3 width, sticky */}
          <div className="lg:col-span-1">
            <div
              ref={bookingCardRef}
              className="bg-white rounded-lg sm:rounded-xl md:rounded-2xl shadow-xl p-3 sm:p-4 md:p-6 lg:sticky lg:top-24"
            >
              {service.packages && service.packages.length > 0 ? (
                <>
                  <div className="mb-3 sm:mb-4 md:mb-6">
                    <div className="text-lg sm:text-xl font-semibold text-gray-800 mb-4 text-center">
                      {selectedPackage ? "Package Price" : "Select a Package"}
                    </div>
                    {selectedPackage ? (
                      <>
                        {(() => {
                          const pkg = service.packages.find(
                            (p) => p.id === selectedPackage,
                          );
                          if (!pkg) return null;
                          return (
                            <>
                              {pkg.price_per_person && (
                                <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-blue-600">
                                  €{pkg.price_per_person}
                                  <span className="text-xs sm:text-sm md:text-base lg:text-lg text-gray-600 font-normal ml-1 sm:ml-2">
                                    / person
                                  </span>
                                </div>
                              )}
                              {pkg.price_per_group && (
                                <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-blue-600">
                                  €{pkg.price_per_group}
                                  <span className="text-xs sm:text-sm md:text-base lg:text-lg text-gray-600 font-normal ml-1 sm:ml-2">
                                    / group (1–4 people)
                                  </span>
                                </div>
                              )}
                              <div className="mt-2 bg-blue-50 p-2 rounded-lg">
                                <p className="text-xs text-blue-700 font-semibold">
                                  Package {selectedPackage} Selected
                                </p>
                                <p className="text-xs text-gray-600">
                                  {pkg.duration} - {pkg.name}
                                </p>
                              </div>
                            </>
                          );
                        })()}
                      </>
                    ) : (
                      <div className="text-sm sm:text-base text-gray-500 text-center py-4">
                        Choose a package below
                      </div>
                    )}
                  </div>

                  {isJetSkiRequest ? (
                    <div className="space-y-3 mb-3 sm:mb-4 md:mb-5">
                      <div className="bg-cyan-50 border border-cyan-200 rounded-xl p-4">
                        <p className="text-sm font-bold text-cyan-800 mb-1">
                          � How to Book:
                        </p>
                        <p className="text-sm text-cyan-700 leading-relaxed">
                          Contact us to arrange your jet ski session. We'll
                          confirm availability and tailor the experience to you.
                        </p>
                      </div>
                      <a
                        href="https://wa.me/355692311606?text=Hello%2C%20I'd%20like%20to%20book%20the%20Jet%20Ski%20Adventure%20in%20Durr%C3%ABs"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <button className="w-full flex items-center justify-center gap-2 py-3 sm:py-4 bg-green-500 text-white font-bold text-sm sm:text-base md:text-lg rounded-lg sm:rounded-xl hover:bg-green-600 transition-all shadow-lg mb-2 sm:mb-3">
                          <FaWhatsapp className="text-lg" /> Request via
                          WhatsApp
                        </button>
                      </a>
                      <a href="mailto:seapulsealbania@gmail.com?subject=Jet%20Ski%20Adventure%20Request">
                        <button className="w-full flex items-center justify-center gap-2 py-3 sm:py-4 bg-blue-600 text-white font-bold text-sm sm:text-base md:text-lg rounded-lg sm:rounded-xl hover:bg-blue-700 transition-all shadow-lg">
                          <FaEnvelope className="text-lg" /> Request via Email
                        </button>
                      </a>
                    </div>
                  ) : (
                    <button
                      onClick={() =>
                        selectedPackage &&
                        navigate(`/${slug}/book-now`, {
                          state: { selectedPackage },
                        })
                      }
                      disabled={!selectedPackage}
                      className={`w-full py-3 sm:py-4 md:py-5 font-bold text-base sm:text-lg md:text-xl rounded-lg sm:rounded-xl transition-all transform shadow-lg mb-3 sm:mb-4 md:mb-5 ${
                        selectedPackage
                          ? "bg-blue-600 text-white hover:bg-blue-700 hover:scale-[1.02] active:scale-95 cursor-pointer"
                          : "bg-gray-300 text-gray-500 cursor-not-allowed"
                      }`}
                    >
                      {selectedPackage ? "Book Now" : "Select Package First"}
                    </button>
                  )}
                </>
              ) : (
                <>
                  <div className="mb-3 sm:mb-4 md:mb-6">
                    <div className="text-[10px] sm:text-xs md:text-sm text-gray-600 mb-1 sm:mb-2">
                      From
                    </div>
                    <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-blue-600">
                      €{service.price}
                      {service.priceType && (
                        <span className="text-xs sm:text-sm md:text-base lg:text-lg text-gray-600 font-normal ml-1 sm:ml-2">
                          / {service.priceType}
                        </span>
                      )}
                    </div>
                  </div>

                  {isYachtRequest ? (
                    <div className="space-y-3 mb-3 sm:mb-4 md:mb-5">
                      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                        <p className="text-sm font-bold text-blue-800 mb-1">
                          ⛵ Private Charter Request
                        </p>
                        <p className="text-sm text-blue-700 leading-relaxed">
                          Contact us to arrange your exclusive yacht experience.
                          We'll tailor everything to your needs.
                        </p>
                      </div>
                      <a
                        href="https://wa.me/355692311606?text=Hello%2C%20I'd%20like%20to%20request%20the%20Premium%20Yacht%20Experience%20in%20Durr%C3%ABs"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <button className="w-full flex items-center justify-center gap-2 py-3 sm:py-4 bg-green-500 text-white font-bold text-sm sm:text-base md:text-lg rounded-lg sm:rounded-xl hover:bg-green-600 transition-all shadow-lg mb-2 sm:mb-3">
                          <FaWhatsapp className="text-lg" /> Request via
                          WhatsApp
                        </button>
                      </a>
                      <a href="mailto:seapulsealbania@gmail.com?subject=Yacht%20Charter%20Request">
                        <button className="w-full flex items-center justify-center gap-2 py-3 sm:py-4 bg-blue-600 text-white font-bold text-sm sm:text-base md:text-lg rounded-lg sm:rounded-xl hover:bg-blue-700 transition-all shadow-lg">
                          <FaEnvelope className="text-lg" /> Request via Email
                        </button>
                      </a>
                    </div>
                  ) : (
                    <button
                      onClick={() => navigate(`/${slug}/book-now`)}
                      className="w-full py-3 sm:py-4 md:py-5 bg-blue-600 text-white font-bold text-base sm:text-lg md:text-xl rounded-lg sm:rounded-xl hover:bg-blue-700 transition-all transform hover:scale-[1.02] active:scale-95 shadow-lg mb-3 sm:mb-4 md:mb-5"
                    >
                      Book Now
                    </button>
                  )}
                </>
              )}

              <div className="space-y-2 sm:space-y-3 md:space-y-4 text-xs sm:text-sm md:text-base text-gray-600">
                <div className="flex items-center gap-2 sm:gap-3">
                  <svg
                    className="w-5 h-5 sm:w-6 sm:h-6 text-green-500 flex-shrink-0"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="leading-relaxed">
                    Free cancellation up to 48 hours before
                  </span>
                </div>
                <div className="flex items-center gap-2 sm:gap-3">
                  <svg
                    className="w-5 h-5 sm:w-6 sm:h-6 text-green-500 flex-shrink-0"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="leading-relaxed">Instant confirmation</span>
                </div>
                <div className="pt-2 sm:pt-3">
                  <Link
                    to="/terms-and-conditions"
                    className="text-blue-600 hover:underline text-xs sm:text-sm md:text-base"
                  >
                    View Terms & Conditions
                  </Link>
                </div>
              </div>

              <div className="mt-4 sm:mt-5 md:mt-6 pt-4 sm:pt-5 md:pt-6 border-t border-gray-200 text-center">
                <p className="text-xs sm:text-sm md:text-base text-gray-600 mb-2 sm:mb-3">
                  Have questions?
                </p>
                <button
                  onClick={() => navigate("/contact")}
                  className="text-blue-600 font-semibold hover:underline text-sm sm:text-base md:text-lg"
                >
                  Contact Us
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewBooking;
